import React, { useEffect, useState, Component } from 'react';
import { CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { useCart } from '../contexts/CartContext';
interface CustomizationState {
  [itemId: string]: {
    wantsCustomization: 'yes' | 'no' | '';
    fancyTotUpgrade: boolean;
    fancyTotUpgradeDetails: string;
    includedVinylText: string;
    standardCustomization: boolean;
    standardCustomizationDetails: string;
    ballCustomization: boolean;
    ballCustomizationDetails: string;
    vinylDecals: boolean;
    vinylDecalsQuantity: string;
    vinylDecalsDetails: string;
  };
}
export const BookingRequestPage = () => {
  const {
    items: cartItems,
    clearCart
  } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    partyLocation: '',
    partyCity: '',
    otherLocationDetails: '',
    message: ''
  });
  const [customizations, setCustomizations] = useState<CustomizationState>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [addressError, setAddressError] = useState('');
  const [deliveryCost, setDeliveryCost] = useState<number | null>(null);
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const BASE_ADDRESS = '3186 Garden Hill Lane, Douglasville, GA 30135';
  const MAPQUEST_API_KEY = 'ZUdH7N3IhErM5jO0rtgyCtZ4tWTth0v5';
  const calculateShippingCost = (driveTimeMinutes: number): number => {
    if (driveTimeMinutes <= 30) return 0;
    if (driveTimeMinutes <= 45) return 25;
    if (driveTimeMinutes <= 60) return 50;
    return -1;
  };
  const calculateDeliveryCost = async (address: string) => {
    if (!address || address.length < 5) {
      setDeliveryCost(null);
      return;
    }
    // Check if it's just a ZIP code (5 digits)
    const zipOnlyMatch = address.match(/^\d{5}$/);
    let fullAddress = address;
    if (zipOnlyMatch) {
      // User entered just a ZIP code, append to base city/state for routing
      fullAddress = `${address}, Georgia`;
    }
    // Check if same as base address
    const normalizedInput = address.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedBase = BASE_ADDRESS.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedInput === normalizedBase) {
      setDeliveryCost(0);
      return;
    }
    setIsCalculatingDelivery(true);
    try {
      const url = `https://www.mapquestapi.com/directions/v2/route?key=${MAPQUEST_API_KEY}&from=${encodeURIComponent(BASE_ADDRESS)}&to=${encodeURIComponent(fullAddress)}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.route && data.route.time) {
        const driveTimeMinutes = Math.ceil(data.route.time / 60);
        const shippingCost = calculateShippingCost(driveTimeMinutes);
        setDeliveryCost(shippingCost);
      } else {
        setDeliveryCost(null);
      }
    } catch (error) {
      console.error('Delivery calculation error:', error);
      setDeliveryCost(null);
    } finally {
      setIsCalculatingDelivery(false);
    }
  };
  // Initialize customization state for customizable items
  useEffect(() => {
    const initialCustomizations: CustomizationState = {};
    cartItems.forEach(item => {
      const isFancyTot = item.packageValue === 'fancy-tot' || item.packageValue === 'mega-fancy-tot';
      const isWhiteBounceHouse = item.packageValue === 'medium-white-bounce-house' || item.packageValue === 'combo-white-bounce-house' || item.packageValue === 'mega-white-combo-bounce-house';
      if (isFancyTot || isWhiteBounceHouse) {
        initialCustomizations[item.id] = {
          wantsCustomization: '',
          fancyTotUpgrade: false,
          fancyTotUpgradeDetails: '',
          includedVinylText: '',
          standardCustomization: false,
          standardCustomizationDetails: '',
          ballCustomization: false,
          ballCustomizationDetails: '',
          vinylDecals: false,
          vinylDecalsQuantity: '',
          vinylDecalsDetails: ''
        };
      }
    });
    setCustomizations(initialCustomizations);
  }, [cartItems]);
  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);
  // Calculate delivery when address changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.partyCity && formData.partyCity.length >= 5) {
        calculateDeliveryCost(formData.partyCity);
      }
    }, 1000); // Debounce for 1 second
    return () => clearTimeout(timer);
  }, [formData.partyCity]);
  const validateAddress = (address: string): boolean => {
    const trimmed = address.trim();
    if (trimmed.length < 5) {
      setAddressError('Please enter at least a ZIP code');
      return false;
    }
    setAddressError('');
    return true;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === 'partyCity' && addressError) {
      setAddressError('');
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Parse a time string like "8:00 PM", "8 PM", or "20:00" and return total minutes since midnight (or null if unparseable)
  const parseTimeToMinutes = (timeStr: string): number | null => {
    if (!timeStr) return null;
    const cleaned = timeStr.trim().toUpperCase();
    // Match formats like "8:30 PM", "8 PM", "8:30PM", "8PM"
    const ampmMatch = cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = ampmMatch[2] ? parseInt(ampmMatch[2], 10) : 0;
      const period = ampmMatch[3];
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
    // Match 24-hour format like "20:00"
    const militaryMatch = cleaned.match(/^(\d{1,2}):(\d{2})$/);
    if (militaryMatch) {
      const hours = parseInt(militaryMatch[1], 10);
      const minutes = parseInt(militaryMatch[2], 10);
      return hours * 60 + minutes;
    }
    return null;
  };
  const endTimeMinutes = parseTimeToMinutes(formData.endTime);
  const isEndTimeLate = endTimeMinutes !== null && endTimeMinutes > 19 * 60; // after 7:00 PM
  const handleCustomizationChange = (itemId: string, field: string, value: any) => {
    setCustomizations(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };
  const calculateTotal = () => {
    let total = cartItems.reduce((sum, item) => {
      const priceMatch = item.price.match(/\d+/);
      const price = priceMatch ? parseInt(priceMatch[0]) : 0;
      return sum + price;
    }, 0);
    // Add customization fees
    Object.entries(customizations).forEach(([itemId, custom]) => {
      const item = cartItems.find(i => i.id === itemId);
      const isFancyTot = item?.packageValue === 'fancy-tot' || item?.packageValue === 'mega-fancy-tot';
      if (isFancyTot) {
        // Customizations are INCLUDED in the package price for Fancy Tot / Mega Fancy Tot
        // No extra fees
      } else {
        if (custom.standardCustomization) {
          total += 25;
        }
        if (custom.ballCustomization) {
          total += 15;
        }
        if (custom.vinylDecals && custom.vinylDecalsQuantity) {
          const quantity = parseInt(custom.vinylDecalsQuantity) || 0;
          total += quantity * 25;
        }
      }
    });
    // Add delivery cost if available and not -1
    if (deliveryCost !== null && deliveryCost >= 0) {
      total += deliveryCost;
    }
    return total;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAddress(formData.partyCity)) {
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus({});
    try {
      const serviceId = 'service_wywqkye';
      const templateId = 'template_6d30pxw';
      const publicKey = 'nKRg8BnYcuXf4azVI';
      let packagesText = '';
      if (cartItems.length > 0) {
        packagesText = cartItems.map((item, index) => {
          let text = `${index + 1}. ${item.packageName} - ${item.price}`;
          const custom = customizations[item.id];
          const isFancyTot = item.packageValue === 'fancy-tot' || item.packageValue === 'mega-fancy-tot';
          if (custom && custom.wantsCustomization === 'yes') {
            text += '\n   Customizations:';
            if (isFancyTot) {
              if (custom.standardCustomization) {
                text += `\n   - Standard Customizations: ${custom.standardCustomizationDetails}`;
              }
              if (custom.vinylDecals) {
                text += `\n   - Vinyl Decals: ${custom.vinylDecalsDetails}`;
              }
            } else {
              if (custom.standardCustomization) {
                text += `\n   - Standard Customization (+$25): ${custom.standardCustomizationDetails}`;
              }
              if (custom.ballCustomization) {
                text += `\n   - Ball Customization (+$15): ${custom.ballCustomizationDetails}`;
              }
              if (custom.vinylDecals) {
                text += `\n   - Vinyl Decals (Qty: ${custom.vinylDecalsQuantity}): ${custom.vinylDecalsDetails}`;
              }
            }
          }
          return text;
        }).join('\n');
      }
      const totalPrice = calculateTotal();
      const deliveryText = deliveryCost === null ? 'To be calculated by our team' : deliveryCost === -1 ? 'To be calculated by our team' : deliveryCost === 0 ? 'FREE' : `$${deliveryCost}`;
      // Add delivery cost to packages text
      packagesText += `\n\nDelivery Cost: ${deliveryText}`;
      const formattedData = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate,
        start_time: formData.startTime,
        end_time: formData.endTime,
        location_type: formData.partyLocation,
        location_address: formData.partyCity,
        location_details: formData.otherLocationDetails || 'N/A',
        packages: packagesText,
        total: deliveryCost === null || deliveryCost === -1 ? `$${totalPrice} + delivery (to be calculated)` : `$${totalPrice}`,
        item_count: cartItems.length,
        message: formData.message
      };
      console.log('Sending to EmailJS:', formattedData);
      const result = await emailjs.send(serviceId, templateId, formattedData, publicKey);
      console.log('EmailJS result:', result);
      navigate('/booking-success');
      setTimeout(() => clearCart(), 100);
    } catch (error) {
      console.error('General error:', error);
      setSubmitStatus({
        success: false,
        message: 'There was a problem sending your request. Please try again or contact us directly at info@toddlertimeatl.com.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const hasCustomizableItems = cartItems.some(item => item.packageValue === 'fancy-tot' || item.packageValue === 'mega-fancy-tot' || item.packageValue === 'medium-white-bounce-house' || item.packageValue === 'combo-white-bounce-house' || item.packageValue === 'mega-white-combo-bounce-house');
  return <div className="bg-white w-full min-h-screen" data-id="element-519">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-520">
        <div className="text-center mb-12" data-id="element-521">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-500 leading-tight" data-id="element-522">
            Request Your Booking
          </h1>
          <p className="mt-4 text-lg text-gray-600" data-id="element-523">
            Fill out the details below and we'll confirm availability
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-524">
          {/* Left Column: Form and Customizations */}
          <div className="lg:col-span-2 space-y-8" data-id="element-525">
            {/* Customization Options */}
            {hasCustomizableItems && <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg" data-id="element-526">
                <h2 className="font-semibold text-gray-900 mb-4 text-lg" data-id="element-527">
                  Customization Options
                </h2>
                {cartItems.map(item => {
              const isFancyTot = item.packageValue === 'fancy-tot' || item.packageValue === 'mega-fancy-tot';
              const isWhiteBounceHouse = item.packageValue === 'medium-white-bounce-house' || item.packageValue === 'combo-white-bounce-house' || item.packageValue === 'mega-white-combo-bounce-house';
              if (!isFancyTot && !isWhiteBounceHouse) return null;
              const custom = customizations[item.id] || {
                wantsCustomization: '',
                fancyTotUpgrade: false,
                fancyTotUpgradeDetails: '',
                includedVinylText: '',
                standardCustomization: false,
                standardCustomizationDetails: '',
                ballCustomization: false,
                ballCustomizationDetails: '',
                vinylDecals: false,
                vinylDecalsQuantity: '',
                vinylDecalsDetails: ''
              };
              return <div key={item.id} className="mb-6 last:mb-0" data-id="element-528">
                      <h3 className="font-medium text-gray-900 mb-3" data-id="element-529">
                        {item.packageName}
                      </h3>

                      <div className="mb-4" data-id="element-530">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-531">
                          Would you like any customizations?
                        </label>
                        <select value={custom.wantsCustomization} onChange={e => handleCustomizationChange(item.id, 'wantsCustomization', e.target.value)} className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-532">
                          <option value="" data-id="element-533">Select an option</option>
                          <option value="no" data-id="element-534">No</option>
                          <option value="yes" data-id="element-535">Yes</option>
                        </select>
                      </div>

                      {custom.wantsCustomization === 'yes' && <div className="ml-4 space-y-4 p-4 bg-white rounded-md border border-purple-200" data-id="element-536">
                          {isFancyTot && <>
                              <div data-id="element-537">
                                <label className="flex items-start" data-id="element-538">
                                  <input type="checkbox" checked={custom.standardCustomization} onChange={e => {
                          handleCustomizationChange(item.id, 'standardCustomization', e.target.checked);
                          if (!e.target.checked) {
                            handleCustomizationChange(item.id, 'standardCustomizationDetails', '');
                          }
                        }} className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500" data-id="element-539" />
                                  <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-540">
                                    Standard Customizations (ball colors, animal
                                    colors, mat color)
                                  </span>
                                </label>

                                {custom.standardCustomization && <div className="mt-3 ml-6" data-id="element-541">
                                    <label className="block text-sm text-gray-600 mb-1" data-id="element-542">
                                      Please specify your customization
                                      requests:
                                    </label>
                                    <textarea value={custom.standardCustomizationDetails} onChange={e => handleCustomizationChange(item.id, 'standardCustomizationDetails', e.target.value)} rows={3} placeholder="e.g., Pink and white balls, blue mat, pastel animals" className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-543" />
                                  </div>}
                              </div>

                              <div data-id="element-544">
                                <label className="flex items-start" data-id="element-545">
                                  <input type="checkbox" checked={custom.vinylDecals} onChange={e => {
                          handleCustomizationChange(item.id, 'vinylDecals', e.target.checked);
                          if (!e.target.checked) {
                            handleCustomizationChange(item.id, 'vinylDecalsDetails', '');
                          }
                        }} className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500" data-id="element-546" />
                                  <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-547">
                                    Vinyl Decals
                                  </span>
                                </label>

                                {custom.vinylDecals && <div className="mt-3 ml-6" data-id="element-548">
                                    <label className="block text-sm text-gray-600 mb-1" data-id="element-549">
                                      Describe the vinyls you'd like:
                                    </label>
                                    <textarea value={custom.vinylDecalsDetails} onChange={e => handleCustomizationChange(item.id, 'vinylDecalsDetails', e.target.value)} rows={3} placeholder={'e.g., "Charlie\'s fairy first" in pink script'} className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-550" />
                                  </div>}
                              </div>
                            </>}

                          {isWhiteBounceHouse && item.packageValue !== 'mega-white-combo-bounce-house' && <div data-id="element-551">
                                <label className="flex items-start" data-id="element-552">
                                  <input type="checkbox" checked={custom.ballCustomization} onChange={e => {
                        handleCustomizationChange(item.id, 'ballCustomization', e.target.checked);
                        if (!e.target.checked) {
                          handleCustomizationChange(item.id, 'ballCustomizationDetails', '');
                        }
                      }} className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500" data-id="element-553" />
                                  <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-554">
                                    Ball Customization (+$15)
                                  </span>
                                </label>

                                {custom.ballCustomization && <div className="mt-3 ml-6" data-id="element-555">
                                    <label className="block text-sm text-gray-600 mb-1" data-id="element-556">
                                      Please specify your ball color
                                      preferences:
                                    </label>
                                    <textarea value={custom.ballCustomizationDetails} onChange={e => handleCustomizationChange(item.id, 'ballCustomizationDetails', e.target.value)} rows={2} placeholder="e.g., Pink and white balls only" className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-557" />
                                  </div>}
                              </div>}

                          {isWhiteBounceHouse && <div data-id="element-558">
                              <label className="flex items-start" data-id="element-559">
                                <input type="checkbox" checked={custom.vinylDecals} onChange={e => {
                        handleCustomizationChange(item.id, 'vinylDecals', e.target.checked);
                        if (!e.target.checked) {
                          handleCustomizationChange(item.id, 'vinylDecalsQuantity', '');
                          handleCustomizationChange(item.id, 'vinylDecalsDetails', '');
                        }
                      }} className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500" data-id="element-560" />
                                <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-561">
                                  Vinyl Decals ($25 per decal)
                                </span>
                              </label>

                              {custom.vinylDecals && <div className="mt-3 ml-6 space-y-3" data-id="element-562">
                                  <div data-id="element-563">
                                    <label className="block text-sm text-gray-600 mb-1" data-id="element-564">
                                      Quantity (how many decals?):
                                    </label>
                                    <input type="number" min="1" value={custom.vinylDecalsQuantity} onChange={e => handleCustomizationChange(item.id, 'vinylDecalsQuantity', e.target.value)} placeholder="e.g., 2" className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-565" />
                                  </div>
                                  <div data-id="element-566">
                                    <label className="block text-sm text-gray-600 mb-1" data-id="element-567">
                                      Decal details:
                                    </label>
                                    <textarea value={custom.vinylDecalsDetails} onChange={e => handleCustomizationChange(item.id, 'vinylDecalsDetails', e.target.value)} rows={3} placeholder="e.g., Child's name 'Emma' in pink script font, Birthday number '3' in gold" className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-568" />
                                  </div>
                                </div>}
                            </div>}
                        </div>}
                    </div>;
            })}
              </div>}

            {submitStatus.message && <div className={`p-4 rounded-md ${submitStatus.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-569">
                {submitStatus.message}
              </div>}

            {/* Booking Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 border border-gray-100 space-y-6" data-id="element-570">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-571">
                <div data-id="element-572">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-573">
                    Full Name
                  </label>
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-574" />
                </div>

                <div data-id="element-575">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-576">
                    Email
                  </label>
                  <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-577" />
                </div>

                <div data-id="element-578">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-579">
                    Phone
                  </label>
                  <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-580" />
                </div>

                <div data-id="element-581">
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700" data-id="element-582">
                    Event Date
                  </label>
                  <input type="date" name="eventDate" id="eventDate" value={formData.eventDate} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-583" />
                </div>

                <div data-id="element-584">
                  <label htmlFor="startTime" className="block text-sm font-medium text-gray-700" data-id="element-585">
                    Start Time
                  </label>
                  <input type="text" name="startTime" id="startTime" value={formData.startTime} onChange={handleChange} placeholder="e.g. 2:00 PM" required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-586" />
                </div>

                <div data-id="element-587">
                  <label htmlFor="endTime" className="block text-sm font-medium text-gray-700" data-id="element-588">
                    End Time
                  </label>
                  <input type="text" name="endTime" id="endTime" value={formData.endTime} onChange={handleChange} placeholder="e.g. 4:00 PM" required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-589" />
                  {isEndTimeLate && <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-id="element-590">
                      <span className="font-semibold" data-id="element-591">Heads up:</span> Our
                      latest pick-up time is 7:00 PM. Please choose an end time
                      at or before 7:00 PM, or contact us to discuss options.
                    </div>}
                </div>

                <div data-id="element-592">
                  <label htmlFor="partyLocation" className="block text-sm font-medium text-gray-700" data-id="element-593">
                    Event Location Type
                  </label>
                  <select name="partyLocation" id="partyLocation" value={formData.partyLocation} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-594">
                    <option value="" data-id="element-595">Select location</option>
                    <option value="home-outside" data-id="element-596">Home (Outside)</option>
                    <option value="home-inside" data-id="element-597">Home (Inside)</option>
                    <option value="event-space" data-id="element-598">Event Space</option>
                    <option value="community-center" data-id="element-599">Community Center</option>
                    <option value="public-park" data-id="element-600">Public Park or Pavilion</option>
                    <option value="other" data-id="element-601">Other</option>
                  </select>
                </div>

                <div data-id="element-602">
                  <label htmlFor="partyCity" className="block text-sm font-medium text-gray-700" data-id="element-603">
                    Event Location
                  </label>
                  <input type="text" name="partyCity" id="partyCity" value={formData.partyCity} onChange={handleChange} required placeholder="Enter your address or ZIP code" className={`mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 rounded-md ${addressError ? 'border-red-500' : 'border-gray-300'}`} data-id="element-604" />
                  {addressError ? <p className="mt-1 text-xs text-red-600" data-id="element-605">{addressError}</p> : <p className="mt-1 text-xs text-gray-500" data-id="element-606">
                      Enter your address or ZIP code to check delivery cost
                    </p>}
                </div>
              </div>

              {formData.partyLocation === 'other' && <div data-id="element-607">
                  <label htmlFor="otherLocationDetails" className="block text-sm font-medium text-gray-700" data-id="element-608">
                    Please Describe the Location
                  </label>
                  <input type="text" name="otherLocationDetails" id="otherLocationDetails" value={formData.otherLocationDetails} onChange={handleChange} required className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-609" />
                </div>}

              <div data-id="element-610">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700" data-id="element-611">
                  Additional Details
                </label>
                <textarea name="message" id="message" rows={4} value={formData.message} onChange={handleChange} required placeholder="Tell us about your event, space requirements, or any special requests..." className="mt-1 py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border border-gray-300 rounded-md" data-id="element-612"></textarea>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-75" data-id="element-613">
                {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
              </button>
            </form>
          </div>

          {/* Right Column: Cart Summary (Sticky) */}
          <div className="lg:col-span-1" data-id="element-614">
            <div className="sticky top-8" data-id="element-615">
              {cartItems.length > 0 && <div className="p-6 bg-primary-50 border border-primary-200 rounded-lg shadow-lg" data-id="element-616">
                  <h2 className="font-semibold text-gray-900 mb-4 text-lg" data-id="element-617">
                    Your Selected Packages
                  </h2>
                  <div className="space-y-3" data-id="element-618">
                    {cartItems.map((item, index) => <div key={item.id} className="flex justify-between items-start" data-id="element-619">
                        <div className="flex-grow" data-id="element-620">
                          <span className="font-medium text-gray-900 text-sm" data-id="element-621">
                            {index + 1}. {item.packageName}
                          </span>
                        </div>
                        <span className="font-semibold text-green-800 ml-4 text-sm" data-id="element-622">
                          {item.price}
                        </span>
                      </div>)}
                    {Object.entries(customizations).map(([itemId, custom]) => {
                  const item = cartItems.find(i => i.id === itemId);
                  const isFancyTot = item?.packageValue === 'fancy-tot' || item?.packageValue === 'mega-fancy-tot';
                  const fees = [];
                  if (isFancyTot) {
                    // Customizations are included in the package price — no fees shown
                  } else {
                    if (custom.standardCustomization) {
                      fees.push(<div key={`custom-standard-${itemId}`} className="flex justify-between items-start ml-4" data-id="element-623">
                              <span className="text-xs text-gray-600" data-id="element-624">
                                Standard Customization
                              </span>
                              <span className="font-semibold text-green-800 text-xs" data-id="element-625">
                                +$25
                              </span>
                            </div>);
                    }
                    if (custom.ballCustomization) {
                      fees.push(<div key={`custom-ball-${itemId}`} className="flex justify-between items-start ml-4" data-id="element-626">
                              <span className="text-xs text-gray-600" data-id="element-627">
                                Ball Customization
                              </span>
                              <span className="font-semibold text-green-800 text-xs" data-id="element-628">
                                +$15
                              </span>
                            </div>);
                    }
                    if (custom.vinylDecals && custom.vinylDecalsQuantity) {
                      const quantity = parseInt(custom.vinylDecalsQuantity) || 0;
                      const vinylCost = quantity * 25;
                      fees.push(<div key={`custom-vinyl-${itemId}`} className="flex justify-between items-start ml-4" data-id="element-629">
                              <span className="text-xs text-gray-600" data-id="element-630">
                                Vinyl Decals (x{quantity})
                              </span>
                              <span className="font-semibold text-green-800 text-xs" data-id="element-631">
                                +${vinylCost}
                              </span>
                            </div>);
                    }
                  }
                  return fees;
                })}

                    {/* Delivery Cost */}
                    {formData.partyCity && <div className="flex justify-between items-start mt-2 pt-2 border-t border-primary-200" data-id="element-632">
                        <span className="text-sm text-gray-600" data-id="element-633">Delivery</span>
                        <span className="font-semibold text-green-800 text-sm" data-id="element-634">
                          {isCalculatingDelivery ? 'Calculating...' : deliveryCost === null ? 'TBD' : deliveryCost === -1 ? 'TBD' : deliveryCost === 0 ? 'FREE' : `+$${deliveryCost}`}
                        </span>
                      </div>}
                  </div>
                  <div className="mt-4 pt-4 border-t border-primary-300 flex justify-between font-bold text-lg" data-id="element-635">
                    <span data-id="element-636">Estimated Total:</span>
                    <span className="text-green-800" data-id="element-637">
                      ${calculateTotal()}
                      {(deliveryCost === null || deliveryCost === -1) && formData.partyCity && !isCalculatingDelivery && <span className="text-xs font-normal text-gray-600 ml-1 block" data-id="element-638">
                            + delivery (TBD)
                          </span>}
                    </span>
                  </div>
                  {(deliveryCost === null || deliveryCost === -1) && formData.partyCity && !isCalculatingDelivery && <p className="text-xs text-gray-600 mt-2" data-id="element-639">
                        Delivery fee will be calculated by our team based on
                        your location
                      </p>}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};